import { createMRTColumnHelper, MRT_ColumnDef } from 'material-react-table'
import { useMemo } from 'react'

import { TextCell } from '@/components'
import { createHeaderWithTooltip } from '@/components/MrtTable/helpers'
import { LANGUAGE_KEY } from '@/context/Locale/Locale'
import { useAppContext } from '@/hooks'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { RelationDto, RelationStatus, RelationType } from '@/rtkApi/mddApiSliceGeneratedV2'

import { DependenciesActions, DependenciesLinkCell } from '../components'
import {
	RelationNameCell,
	RelationStatusCell,
	RelationTypeCell,
} from '../components/RelationCells'

const relationTypeKey = (type: RelationType): LANGUAGE_KEY => {
	switch (type) {
		case 'CUSTOM_LINK':
			return 'RELATION_TYPE_CUSTOM_LINK'
		case 'LINK':
			return 'RELATION_TYPE_LINK'
		case 'LINK_COLUMN':
			return 'RELATION_TYPE_LINK_COLUMN'
		case 'MAPPING_WRITE':
			return 'RELATION_TYPE_MAPPING_WRITE'
		case 'PK_FK_COLUMN':
			return 'RELATION_TYPE_PK_FK_COLUMN'
		case 'PK_FK_TABLE':
			return 'RELATION_TYPE_PK_FK_TABLE'
	}
}

const relationStatusKey = (status: RelationStatus): LANGUAGE_KEY => {
	switch (status) {
		case 'BROKEN':
			return 'RELATION_STATUS_BROKEN'
		case 'FK_COL_NOT_SET':
			return 'RELATION_STATUS_FK_COL_NOT_SET'
		case 'OK':
			return 'RELATION_STATUS_OK'
		case 'PK_COL_REMOVED':
			return 'RELATION_STATUS_PK_COL_REMOVED'
		case 'PK_SIDE_REMOVED':
			return 'RELATION_STATUS_PK_SIDE_REMOVED'
	}
}

export const useDepColumns = () => {
	const { t } = useAppContext()
	const {
		state: { node, editMode },
	} = useDetailTabContext()

	const columnHelper = createMRTColumnHelper<RelationDto>()

	const columns = useMemo(
		() => [
			columnHelper.accessor('name', {
				...createHeaderWithTooltip(t('NAME')),
				size: 260,
				Cell: ({ row }) => <RelationNameCell row={row} />,
			}),
			columnHelper.accessor('code', {
				...createHeaderWithTooltip(t('CODE')),
				Cell: ({ cell, table, row, column }) => (
					<TextCell
						cell={cell}
						column={column}
						table={table}
						row={row}
						isReadOnly
						isDisabled={!editMode}
					/>
				),
			}),
			columnHelper.accessor('description', {
				...createHeaderWithTooltip(t('DESCRIPTION')),
				Cell: ({ cell, table, row, column }) => (
					<TextCell
						cell={cell}
						column={column}
						table={table}
						row={row}
						isReadOnly
						isDisabled={!editMode}
					/>
				),
			}),
			columnHelper.accessor('relationType', {
				...createHeaderWithTooltip(t('TYPE')),
				Cell: ({ row }) => (
					<RelationTypeCell
						label={
							row.original.relationType
								? t(relationTypeKey(row.original.relationType))
								: undefined
						}
					/>
				),
			}),
			columnHelper.accessor('status', {
				...createHeaderWithTooltip(t('STATUS')),
				Cell: ({ row }) => (
					<RelationStatusCell
						label={
							row.original.status
								? t(relationStatusKey(row.original.status))
								: ''
						}
						status={row.original.status}
					/>
				),
			}),
			columnHelper.accessor('rightSideStructure', {
				...createHeaderWithTooltip(t('ELEMENT')),
				size: 100,
				Cell: ({ row }) => <DependenciesLinkCell row={row} node={node} />,
			}),
			columnHelper.accessor('rightSideColumn', {
				header: '',
				size: 50,
				enableColumnActions: false,
				enableSorting: false,
				Cell: ({ row }) => <DependenciesActions row={row} />,
			}),
		],
		[columnHelper, editMode, node, t],
	)

	return columns as MRT_ColumnDef<RelationDto>[]
}
